import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Paperclip, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

/**
 * Build / email a building records pack:
 * CS + drawings + Form 12A/S-RaD/Defect + inspection reports + Picture +
 * checklist Q&A + history summary + selectable Documents + uploads.
 */
export default function BuildingDataPackModal({ buildingId, buildingName, onClose }) {
  const queryClient = useQueryClient();
  const [includeCs, setIncludeCs] = useState(true);
  const [includeDrawings, setIncludeDrawings] = useState(true);
  const [includePaperwork, setIncludePaperwork] = useState(true);
  const [includeInspectionReports, setIncludeInspectionReports] = useState(true);
  const [includePictures, setIncludePictures] = useState(true);
  const [includeChecklistQa, setIncludeChecklistQa] = useState(true);
  const [includeInspections, setIncludeInspections] = useState(true);
  const [inspectionDays, setInspectionDays] = useState(365);
  const [selectedDocIds, setSelectedDocIds] = useState(() => new Set());
  const [to, setTo] = useState('');
  const [notes, setNotes] = useState('');
  const [extras, setExtras] = useState([]);

  const docsQuery = useQuery({
    queryKey: ['data-pack-docs', buildingId, inspectionDays],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/data-pack/documents`, {
        params: { days: Math.max(inspectionDays, 365) },
        skipErrorToast: true,
      });
      return res.data?.data || [];
    },
    enabled: !!buildingId,
  });

  const selectableDocs = useMemo(() => docsQuery.data || [], [docsQuery.data]);

  const toggleDoc = (id) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: async ({ sendEmail }) => {
      const fd = new FormData();
      fd.append('include_cs', includeCs ? '1' : '0');
      fd.append('include_drawings', includeDrawings ? '1' : '0');
      fd.append('include_paperwork', includePaperwork ? '1' : '0');
      fd.append('include_inspection_reports', includeInspectionReports ? '1' : '0');
      fd.append('include_pictures', includePictures ? '1' : '0');
      fd.append('include_checklist_qa', includeChecklistQa ? '1' : '0');
      fd.append('include_inspections', includeInspections ? '1' : '0');
      fd.append('inspection_days', String(inspectionDays || 365));
      fd.append('document_ids', JSON.stringify([...selectedDocIds]));
      if (notes.trim()) fd.append('notes', notes.trim());
      if (sendEmail && to.trim()) fd.append('to', to.trim());
      for (const f of extras) fd.append('extras', f);
      const res = await api.post(`/buildings/${buildingId}/data-pack`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 0,
        skipErrorToast: true,
      });
      return res.data?.data;
    },
    onSuccess: (data, vars) => {
      const mailNote = vars.sendEmail
        ? data?.mail?.stubbed
          ? ' (email stubbed — check SMTP)'
          : data?.mailed
            ? ' and emailed'
            : ''
        : '';
      toast.success(`Pack saved${mailNote}: ${data?.original_name || 'PDF'}`);
      queryClient.invalidateQueries(['docs-browse', `building-${buildingId}`]);
      queryClient.invalidateQueries(['building', buildingId, 'paperwork']);
      queryClient.invalidateQueries({ queryKey: ['data-pack-logs', buildingId] });
      onClose?.(data);
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Pack failed'),
  });

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[95vh] overflow-auto flex flex-col">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Building data pack</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {buildingName || 'Building'} — paperwork, reports, photos, Q&A, optional uploads → one PDF
            </p>
          </div>
          <button type="button" onClick={() => onClose?.()} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 text-sm">
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Include</legend>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={includeCs} onChange={(e) => setIncludeCs(e.target.checked)} />
              Compliance Schedule (from Documents)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeDrawings}
                onChange={(e) => setIncludeDrawings(e.target.checked)}
              />
              Floor drawings
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includePaperwork}
                onChange={(e) => setIncludePaperwork(e.target.checked)}
              />
              Form 12A / S-RaD / Defect HTML
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInspectionReports}
                onChange={(e) => setIncludeInspectionReports(e.target.checked)}
              />
              Inspection report HTML (+ embedded photos)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includePictures}
                onChange={(e) => setIncludePictures(e.target.checked)}
              />
              Picture folder photos
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeChecklistQa}
                onChange={(e) => setIncludeChecklistQa(e.target.checked)}
              />
              Checklist Q&amp;A (from unit states)
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeInspections}
                onChange={(e) => setIncludeInspections(e.target.checked)}
              />
              Inspection history summary (text)
            </label>
            <label className="block pl-6 text-xs text-gray-600">
              Look-back period
              <select
                className="ml-2 border rounded px-2 py-1 text-sm"
                value={inspectionDays}
                onChange={(e) => setInspectionDays(Number(e.target.value))}
              >
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>365 days</option>
                <option value={730}>2 years</option>
              </select>
            </label>
          </fieldset>

          <div>
            <span className="text-xs font-semibold text-gray-600">Select Documents to attach</span>
            <div className="mt-2 max-h-40 overflow-auto border border-gray-200 rounded-lg divide-y">
              {docsQuery.isLoading ? (
                <p className="px-3 py-2 text-xs text-gray-500">Loading documents…</p>
              ) : !selectableDocs.length ? (
                <p className="px-3 py-2 text-xs text-gray-500">No documents in period</p>
              ) : (
                selectableDocs.map((d) => (
                  <label key={d.id} className="flex items-start gap-2 px-3 py-2 text-xs hover:bg-gray-50">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selectedDocIds.has(d.id)}
                      onChange={() => toggleDoc(d.id)}
                    />
                    <span>
                      <span className="font-medium text-gray-800">{d.name}</span>
                      <span className="block text-gray-500">
                        {d.category || '—'}
                        {d.upload_date ? ` · ${String(d.upload_date).slice(0, 10)}` : ''}
                      </span>
                    </span>
                  </label>
                ))
              )}
            </div>
            {selectedDocIds.size ? (
              <p className="mt-1 text-xs text-gray-500">{selectedDocIds.size} selected</p>
            ) : null}
          </div>

          <label className="block">
            <span className="text-xs font-semibold text-gray-600">Upload extra files (PDF / JPG / PNG / HTML)</span>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,.jpg,.jpeg,.png,.html,.htm"
              multiple
              className="mt-1 block w-full text-sm"
              onChange={(e) => setExtras(Array.from(e.target.files || []))}
            />
            {extras.length ? (
              <ul className="mt-2 text-xs text-gray-600 space-y-0.5">
                {extras.map((f) => (
                  <li key={f.name} className="inline-flex items-center gap-1">
                    <Paperclip className="w-3 h-3" />
                    {f.name}
                  </li>
                ))}
              </ul>
            ) : null}
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-gray-600">Notes (on cover page)</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="e.g. Requested by council 22 Sep 2026"
            />
          </label>

          <label className="block">
            <span className="text-xs font-semibold text-gray-600">Email to (optional)</span>
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="leave blank to save PDF only"
            />
          </label>
        </div>

        <div className="px-4 py-3 border-t flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="px-3 py-2 rounded-lg border text-sm font-medium text-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate({ sendEmail: false })}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold disabled:opacity-50"
          >
            <FileText className="w-4 h-4" />
            {mutation.isPending ? 'Working…' : 'Save PDF only'}
          </button>
          <button
            type="button"
            disabled={mutation.isPending || !to.trim()}
            onClick={() => mutation.mutate({ sendEmail: true })}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {mutation.isPending ? 'Sending…' : 'Save & email'}
          </button>
        </div>
      </div>
    </div>
  );
}
